import {
  isOrganizationOwnerMembership,
  OrganizationUserType,
} from '@mfw/common';

import {
  useAuthContext,
  useOrganizationContext,
  usePermissionsContext,
} from '../../context';

/**
 * T4.6 — a TRAINER sees only the sessions they lead, and never any money on
 * them, even when a custom role granted `finance_stats_read`. Ownership
 * follows `isOrganizationOwnerMembership`, shared with the backend: a
 * solo-PT owner holds TRAINER, while an invited colleague is employed.
 *
 * Performance plan 1 (review finding 3) — renamed from
 * `isSessionMoneyHiddenForActor`: since B7 it gates EVERY organization-money
 * view (Overview money, Memberships, Revenue, the ratio tile, the sidebar),
 * not only session money.
 */
export const isOrgMoneyHiddenForActor = (
  orgUserType: OrganizationUserType | undefined,
  isOwner: boolean,
) => orgUserType === OrganizationUserType.TRAINER && !isOwner;

/**
 * The ACTOR-CLASS half alone — no read key: may this account see this
 * organization's money at all? A platform user always (P1/P5); otherwise an
 * ACTIVE member of the organization who is not an employed trainer (any
 * TRAINER row and not the owner — the same rule the BE chokepoint applies).
 * Final review (Important 1): Profits & Expenses gates on its own keys
 * (`finance_stats_read` OR `finance_entries_read`) AND on this.
 */
/** The caller's ACTIVE rows in the organization and whether one makes them
 *  its owner (`isOrganizationOwnerMembership`, shared with the backend);
 *  `null` when the organization is not the one being viewed. */
const useOrgMembership = (organizationId?: string) => {
  const { currentUser } = useAuthContext();
  const { currentOrganization } = useOrganizationContext();
  const targetId = organizationId ?? currentOrganization?.id;
  if (!currentUser?.id || !targetId || currentOrganization?.id !== targetId) {
    return null;
  }
  const memberships = (currentUser.organizationUsers ?? []).filter(
    (row) =>
      row.organizationId === targetId &&
      row.tenantId === currentUser.tenantId &&
      row.status === 'ACTIVE',
  );
  const isOwner = memberships.some((row) =>
    isOrganizationOwnerMembership({
      membershipType: row.type,
      organizationCreatedBy: currentOrganization?.createdBy,
      organizationType: currentOrganization?.type,
      userId: currentUser.id,
    }),
  );
  return { isOwner, memberships };
};

export const useOrgMoneyAllowed = (organizationId?: string) => {
  const { currentUser } = useAuthContext();
  const membership = useOrgMembership(organizationId);
  if (currentUser?.isPlatformUser) return true;
  if (!membership) return false;
  const isTrainer = membership.memberships.some(
    (row) => row.type === OrganizationUserType.TRAINER,
  );
  return (
    membership.memberships.length > 0 &&
    !isOrgMoneyHiddenForActor(
      isTrainer ? OrganizationUserType.TRAINER : undefined,
      membership.isOwner,
    )
  );
};

/**
 * The FE half of `StatsOrgOwnershipService.canReadOrgAggregates` (the
 * organization KPI, `stats/kpi/org`): a platform user (P5), or the OWNER of
 * the organization. An employed trainer or a staff member is refused there,
 * so they are never sent (smoke test 09.10.2026). The read key is the
 * caller's to check.
 */
export const useOwnsOrganization = (organizationId?: string) => {
  const { currentUser } = useAuthContext();
  const membership = useOrgMembership(organizationId);
  if (currentUser?.isPlatformUser) return true;
  return Boolean(membership?.isOwner);
};

/** `finance_stats_read` AND not an employed trainer — the FE half of the
 *  actor-class rule the BE enforces in `StatsOrgOwnershipService`. */
export const useCanReadOrgMoney = (organizationId?: string) => {
  const { hasPermission } = usePermissionsContext();
  const allowed = useOrgMoneyAllowed(organizationId);
  return hasPermission('finance_stats_read') && allowed;
};

/**
 * MV2-577 Part 2 (E9) — Business & Finance → Trainer earnings: the FE half of
 * the report's gate. Its OWN key, `trainer_earnings_read` (owner, trainer
 * owner, front desk) — never `finance_stats_read`, which a front desk does not
 * hold — or a platform user (P5: the guard bypasses, their key list proves
 * nothing either way), AND the money actor class above, so an employed
 * trainer is refused even when a custom role granted the key (the BE refuses
 * both routes to them).
 *
 * Deliberately NOT the fee-INPUT visibility (`useTrainerFeesVisible`, a
 * roster, `trainer_fees_manage`): earned history stays readable after every
 * other trainer has left, in a now-solo organization, or when the roster
 * cannot be read.
 */
export const useCanReadTrainerEarnings = (organizationId?: string) => {
  const { currentUser } = useAuthContext();
  const { hasPermission } = usePermissionsContext();
  const allowed = useOrgMoneyAllowed(organizationId);
  return (
    allowed &&
    (Boolean(currentUser?.isPlatformUser) ||
      hasPermission('trainer_earnings_read'))
  );
};
